<template>
  <div class="notifications-page">
    <header class="notifications-header">
      <div>
        <p class="eyebrow">Alertes</p>
        <h1>Notifications</h1>
        <p>{{ totalCount }} alerte{{ totalCount > 1 ? 's' : '' }} pour les filtres actifs</p>
      </div>
      <button class="icon-button" type="button" aria-label="Actualiser" @click="resetAndFetch">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <path d="M21 3v6h-6" />
        </svg>
      </button>
    </header>

    <section class="notification-toolbar">
      <label class="filter-search">
        Rechercher
        <div class="field-shell">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input v-model.trim="filters.search" type="search" placeholder="Employé ou poste..." @input="debouncedFetch" />
        </div>
      </label>

      <label>
        Statut
        <select v-model="filters.status" @change="resetAndFetch">
          <option value="pending">Nouvelles</option>
          <option value="seen">Lues</option>
          <option value="all">Tout afficher</option>
        </select>
      </label>

      <label>
        Type
        <select v-model="filters.type" @change="resetAndFetch">
          <option value="all">Tous les types</option>
          <option value="missing_entry">Entrée manquante</option>
          <option value="late_entry">Retard</option>
          <option value="auto_checkout">Sortie automatique</option>
          <option value="auto_absence">Absence automatique</option>
          <option value="cashier_advance">Avance caissier</option>
        </select>
      </label>

      <label>
        Poste
        <select v-model="filters.post" @change="resetAndFetch">
          <option value="">Tous les postes</option>
          <option v-for="post in posts" :key="post.Post_name" :value="post.Post_name">{{ post.Post_name }}</option>
        </select>
      </label>

      <div class="day-filter">
        <span>Jour</span>
        <div class="day-nav">
          <button class="day-btn" type="button" aria-label="Jour précédent" @click="shiftDay(-1)">‹</button>
          <label class="date-picker-button">
            <span>{{ selectedDayLabel }}</span>
            <input v-model="selectedDay" type="date" aria-label="Choisir une date" @change="setSelectedDay(selectedDay)" />
          </label>
          <button class="day-btn" type="button" aria-label="Jour suivant" @click="shiftDay(1)">›</button>
        </div>
      </div>
    </section>

    <section class="notifications-list">
      <div class="list-title">
        <div>
          <p class="eyebrow">Liste</p>
          <h2>{{ listTitle }}</h2>
        </div>
        <span>{{ notifications.length }} / {{ totalCount }}</span>
      </div>

      <div v-if="loading && !notifications.length" class="empty-state">Chargement...</div>
      <div v-else-if="error" class="empty-state error-state">{{ error }}</div>
      <div v-else-if="!notifications.length" class="empty-state">Aucune notification pour ces filtres.</div>

      <div v-else class="notification-stack">
        <article
          v-for="item in notifications"
          :key="item.id"
          class="notification-card"
          :class="[`severity-${item.severity}`, `status-${item.status}`]"
        >
          <img class="staff-avatar" :src="staffImage(item)" :alt="`${item.first_name} ${item.last_name}`" />
          <div class="notification-content">
            <div class="card-topline">
              <span class="type-icon">{{ typeIcon(item.notification_type) }}</span>
              <span class="type-pill">{{ typeLabel(item.notification_type) }}</span>
              <span class="status-pill">{{ statusLabel(item.status) }}</span>
            </div>
            <h3>{{ item.first_name }} {{ item.last_name }}</h3>
            <p>{{ item.message }}</p>
            <div class="meta-grid">
              <span>{{ item.post_name || 'Poste non défini' }}</span>
              <span>{{ formatDate(item.work_date) }}</span>
              <span v-if="item.seen_at">Vue {{ formatDateTime(item.seen_at) }}</span>
            </div>
            <div class="card-actions">
              <button class="btn-secondary" type="button" @click="openNotificationTarget(item)">
                {{ item.notification_type === 'cashier_advance' ? 'Avances' : 'Pointage' }}
              </button>
              <button class="btn-primary" type="button" :disabled="item.status === 'seen'" @click="readNotification(item)">
                Lu
              </button>
            </div>
          </div>
        </article>
      </div>

      <div ref="notificationSentinel" class="load-sentinel"></div>
      <div v-if="loadingMore" class="load-state">Chargement plus...</div>
      <div v-else-if="notifications.length && !hasMore" class="load-state">Toutes les notifications sont affichées</div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'AppNotifications',
  data() {
    const today = this.dateToInput(new Date());
    return {
      notifications: [],
      posts: [],
      loading: false,
      loadingMore: false,
      hasMore: true,
      page: 1,
      limit: 10,
      totalCount: 0,
      observer: null,
      error: '',
      searchTimer: null,
      selectedDay: today,
      filters: {
        status: 'pending',
        type: 'all',
        post: '',
        date_from: today,
        date_to: today,
        search: '',
      },
    };
  },
  computed: {
    listTitle() {
      if (this.filters.status === 'seen') return 'Notifications lues';
      if (this.filters.status === 'all') return 'Historique';
      return 'Nouvelles notifications';
    },
    selectedDayLabel() {
      const selected = this.parseInputDate(this.selectedDay);
      const today = this.parseInputDate(this.todayInput());
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      if (this.isSameDay(selected, today)) return `Aujourd'hui · ${this.formatInputDay(this.selectedDay)}`;
      if (this.isSameDay(selected, yesterday)) return `Hier · ${this.formatInputDay(this.selectedDay)}`;
      if (this.isSameDay(selected, tomorrow)) return `Demain · ${this.formatInputDay(this.selectedDay)}`;
      return this.formatInputDay(this.selectedDay);
    },
  },
  mounted() {
    this.fetchPosts();
    this.resetAndFetch();
    this.$nextTick(this.setupInfiniteScroll);
    window.addEventListener('pointage-notification-received', this.resetAndFetch);
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('pointage-notification-received', this.resetAndFetch);
  },
  methods: {
    setupInfiniteScroll() {
      if (!this.$refs.notificationSentinel || this.observer) return;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.fetchNotifications({ append: true });
        }
      }, { rootMargin: '220px 0px' });
      this.observer.observe(this.$refs.notificationSentinel);
    },
    todayInput() {
      return this.dateToInput(new Date());
    },
    dateToInput(date) {
      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      return localDate.toISOString().slice(0, 10);
    },
    parseInputDate(value) {
      const [year, month, day] = String(value || '').split('-').map(Number);
      if (!year || !month || !day) return new Date();
      return new Date(year, month - 1, day);
    },
    isSameDay(first, second) {
      return first.getFullYear() === second.getFullYear()
        && first.getMonth() === second.getMonth()
        && first.getDate() === second.getDate();
    },
    formatInputDay(value) {
      if (!value) return '';
      const [year, month, day] = String(value).split('-');
      if (!year || !month || !day) return value;
      return `${day}/${month}/${year}`;
    },
    setSelectedDay(day) {
      if (!day) return;
      this.selectedDay = day;
      this.filters.date_from = day;
      this.filters.date_to = day;
      this.resetAndFetch();
    },
    shiftDay(delta) {
      const day = this.parseInputDate(this.selectedDay);
      day.setDate(day.getDate() + delta);
      this.setSelectedDay(this.dateToInput(day));
    },
    debouncedFetch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.resetAndFetch(), 250);
    },
    async fetchPosts() {
      try {
        const res = await fetch('/api-pointage/fetchposts', { credentials: 'include', cache: 'no-store' });
        this.posts = res.ok ? await res.json() : [];
      } catch (err) {
        this.posts = [];
      }
    },
    resetAndFetch() {
      this.page = 1;
      this.hasMore = true;
      this.totalCount = 0;
      this.notifications = [];
      return this.fetchNotifications({ append: false });
    },
    sortNotifications(items) {
      return [...items].sort((a, b) => {
        const statusRank = (value) => (value === 'pending' ? 0 : 1);
        if (statusRank(a.status) !== statusRank(b.status)) return statusRank(a.status) - statusRank(b.status);
        const dateA = `${a.work_date || ''} ${a.created_at || ''}`;
        const dateB = `${b.work_date || ''} ${b.created_at || ''}`;
        if (dateA !== dateB) return dateA < dateB ? 1 : -1;
        return Number(b.id || 0) - Number(a.id || 0);
      });
    },
    async fetchNotifications({ append = false } = {}) {
      if ((append && !this.hasMore) || this.loading || this.loadingMore) return;
      if (append) this.loadingMore = true;
      else this.loading = true;
      this.error = '';

      const params = new URLSearchParams();
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      params.set('page', String(this.page));
      params.set('limit', String(this.limit));

      try {
        const res = await fetch(`/api-pointage/notifications?${params.toString()}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok || !contentType.includes('application/json')) {
          throw new Error('Impossible de charger les notifications.');
        }
        const responseData = await res.json();
        const data = Array.isArray(responseData) ? responseData : [];
        const total = Number(res.headers.get('X-Total-Count') || data.length || 0);
        const merged = append ? [...this.notifications, ...data] : data;
        const unique = Array.from(new Map(merged.map((item) => [item.id, item])).values());
        this.notifications = this.sortNotifications(unique);
        this.totalCount = total;
        this.hasMore = this.notifications.length < total && data.length > 0;
        if (data.length > 0) this.page += 1;
      } catch (err) {
        this.error = err.message || 'Erreur notifications.';
        if (!append) this.notifications = [];
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    async markSeen(item) {
      if (!item?.id || item.status === 'validated') return;
      await fetch('/api-pointage/notifications/seen', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id }),
      });
    },
    async openNotificationTarget(item) {
      await this.markSeen(item);
      if (item.notification_type === 'cashier_advance') {
        this.$router.push({
          path: '/avances',
          query: { staff_id: item.staff_id, date: item.work_date },
        });
        return;
      }
      this.$router.push({
        path: '/pointage',
        query: { staff_id: item.staff_id, date: item.work_date },
      });
    },
    async readNotification(item) {
      await this.markSeen(item);
      await this.resetAndFetch();
    },
    staffImage(item) {
      const pic = item?.pic || item?.Pic || '';
      if (!pic) return '/pointage/profile/profile.jpg';
      return pic.startsWith('/api-pointage/uploads/') || pic.startsWith('/pointage/') || pic.startsWith('http')
        ? pic
        : `/api-pointage/uploads/${pic}`;
    },
    typeIcon(type) {
      return {
        missing_entry: 'E',
        late_entry: 'R',
        auto_checkout: 'S',
        auto_absence: 'A',
        cashier_advance: 'DH',
      }[type] || '!';
    },
    typeLabel(type) {
      return {
        missing_entry: 'Entrée manquante',
        late_entry: 'Retard',
        auto_checkout: 'Sortie automatique',
        auto_absence: 'Absence automatique',
        cashier_advance: 'Avance caissier',
      }[type] || type;
    },
    statusLabel(status) {
      return {
        pending: 'Nouvelle',
        seen: 'Lue',
        replaced: 'Remplacée',
        validated: 'Validée',
      }[status] || status;
    },
    formatDate(value) {
      if (!value) return '';
      const [year, month, day] = String(value).split('-');
      if (!year || !month || !day) return value;
      return `${day}/${month}/${year}`;
    },
    formatDateTime(value) {
      if (!value) return '';
      const date = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.notifications-page {
  display: grid;
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.notifications-header,
.notification-toolbar,
.notifications-list {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
}

.notifications-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 18px;
}

.eyebrow {
  margin: 0 0 5px;
  color: var(--primary);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

h1,
h2,
h3,
p {
  margin: 0;
}

h1 {
  color: var(--gray-900);
  font-size: 26px;
  line-height: 1.1;
}

.notifications-header p:last-child {
  margin-top: 5px;
  color: var(--gray-500);
  font-size: 13px;
  font-weight: 700;
}

.icon-button {
  width: 42px;
  height: 42px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  color: var(--gray-700);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 42px;
}

.icon-button svg,
.field-shell svg {
  width: 17px;
  height: 17px;
}

.notification-toolbar {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) repeat(3, minmax(130px, 180px));
  align-items: end;
  gap: 10px;
  padding: 12px;
}

.notification-toolbar label,
.day-filter {
  display: grid;
  gap: 6px;
  min-width: 0;
  color: var(--gray-600);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.notification-toolbar input,
.notification-toolbar select {
  width: 100%;
  min-height: 40px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: #fff;
  color: var(--gray-800);
  padding: 0 11px;
  font-size: 14px;
  font-weight: 750;
  text-transform: none;
  min-width: 0;
}

.field-shell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: #fff;
  padding: 0 11px;
}

.field-shell input {
  min-height: auto;
  border: 0;
  padding: 0;
  outline: 0;
}

.day-filter {
  grid-column: 1 / -1;
  justify-self: end;
  width: min(100%, 390px);
}

.day-nav {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 40px;
  gap: 6px;
}

.day-btn,
.date-picker-button {
  min-height: 40px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: #fff;
  color: var(--gray-800);
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
}

.date-picker-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  overflow: hidden;
  text-align: center;
  text-transform: none;
}

.date-picker-button span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date-picker-button input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.day-btn {
  padding: 0;
  font-size: 22px;
}

.notifications-list {
  overflow: hidden;
}

.list-title {
  min-height: 58px;
  padding: 13px 15px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.list-title h2 {
  color: var(--gray-900);
  font-size: 18px;
}

.list-title > span {
  min-height: 30px;
  border-radius: 999px;
  background: var(--primary-glow);
  color: var(--primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
}

.notification-stack {
  display: grid;
  gap: 9px;
  padding: 10px;
}

.notification-card {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--border);
  border-left: 4px solid var(--primary);
  border-radius: 8px;
  background: #fff;
}

.notification-card.severity-high {
  border-left-color: var(--danger);
}

.notification-card.status-seen,
.notification-card.status-validated {
  background: #f8fafc;
}

.staff-avatar {
  width: 54px;
  height: 54px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--gray-100);
  border: 1px solid var(--border);
}

.notification-content {
  min-width: 0;
}

.card-topline,
.meta-grid,
.card-actions {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.type-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: var(--gray-900);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 900;
}

.type-pill,
.status-pill {
  min-height: 24px;
  border-radius: 999px;
  padding: 0 9px;
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 900;
}

.type-pill {
  background: var(--gray-100);
  color: var(--gray-700);
}

.status-pill {
  background: var(--warning-bg);
  color: var(--warning);
}

.status-seen .status-pill {
  background: var(--primary-glow);
  color: var(--primary);
}

.status-validated .status-pill {
  background: var(--success-bg);
  color: var(--success);
}

.notification-card h3 {
  margin-top: 8px;
  color: var(--gray-900);
  font-size: 16px;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.notification-card p {
  margin-top: 5px;
  color: var(--gray-700);
  font-size: 14px;
  line-height: 1.45;
}

.meta-grid {
  margin-top: 10px;
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 800;
}

.card-actions {
  margin-top: 12px;
  justify-content: flex-end;
}

.btn-primary,
.btn-secondary {
  min-height: 38px;
  border-radius: 7px;
  padding: 0 13px;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  border: 1px solid var(--primary);
  background: var(--primary);
  color: #fff;
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn-secondary {
  border: 1px solid var(--border);
  background: #fff;
  color: var(--gray-800);
}

.empty-state,
.load-state {
  padding: 30px 14px;
  text-align: center;
  color: var(--gray-500);
  font-weight: 800;
}

.error-state {
  color: var(--danger);
}

.load-state {
  padding: 14px;
  font-size: 13px;
}

.load-sentinel {
  height: 1px;
}

@media (max-width: 980px) {
  .notification-toolbar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-search {
    grid-column: 1 / -1;
  }
}

@media (max-width: 640px) {
  .notifications-page {
    gap: 10px;
  }

  .notifications-header {
    padding: 14px;
  }

  h1 {
    font-size: 23px;
  }

  .notification-toolbar {
    grid-template-columns: 1fr;
    padding: 10px;
  }

  .day-filter {
    justify-self: stretch;
  }

  .notification-stack {
    padding: 8px;
  }

  .notification-card {
    grid-template-columns: 48px minmax(0, 1fr);
    gap: 10px;
    padding: 10px;
  }

  .staff-avatar {
    width: 48px;
    height: 48px;
  }

  .card-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}
</style>
